import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Keyboard from '../../../services/keyboardShortcuts';
import T, { Translate } from '../../i18n/i18n';
import GridController from './GridController';
import GridNetwork from './GridNetwork';
import Operations from './Util/Operations';
import Dice3d from '../Dice3d/Dice3d';
import { gridtoolsActions, charactersActions, timersActions, turnorderActions } from '../../../redux/actions';
import { Container, ZoomOut, ZoomIn, ZoomIcon, JumpToCenter, GmWarning, TurnOrder, Timers, EventHandler, Enable3D } from './Grid.styles';

export default ({tableId}) => {

    const dispatch = useDispatch();
    const canvasRef = useRef(null);
    const canvasEventRef = useRef(null);
    const gridInstance = useRef(null);
    const networkInstance = useRef(null);

    const gridtoolsTool = useSelector(state => state.gridtoolsState.tool);
    const gridtoolsSnap = useSelector(state => state.gridtoolsState.snap);
    const gridtoolsProps = useSelector(state => state.gridtoolsState.props);
    const gridtoolsGridId = useSelector(state => state.gridtoolsState.gridId);
    const gridtoolsActiveGridId = useSelector(state => state.gridtoolsState.activeGridId);
    const gridtoolsPropsGrid = useSelector(state => state.gridtoolsState.propsGrid);
    const gridtoolsLoadedGrid = useSelector(state => state.gridtoolsState.loadedGrid);
    const gridtoolsSyncRender = useSelector(state => state.gridtoolsState.syncRender);
    const gridtoolsSyncRenderOp = useSelector(state => state.gridtoolsState.syncRenderOp);
    const gridtoolsSyncRenderItems = useSelector(state => state.gridtoolsState.syncRenderItems);
    const gridtoolsSyncPropsGrid = useSelector(state => state.gridtoolsState.syncPropsGrid);
    const gridtoolsSignalBuffer = useSelector(state => state.gridtoolsState.signalBuffer);
    const gridtoolsMeasureBuffer = useSelector(state => state.gridtoolsState.measureBuffer);
    const gridtoolsMeasureClearBuffer = useSelector(state => state.gridtoolsState.measureClearBuffer);
    const gridtoolsMovingBuffer = useSelector(state => state.gridtoolsState.movingBuffer);
    const gridtoolsPropagate = useSelector(state => state.gridtoolsState.propagateTime);
    const characterList = useSelector(state => state.charactersState.characters);
    const characterSeekForToken = useSelector(state => state.charactersState.seekForChar);
    const playerId = useSelector(state => state.authState.id);
    const playerColor = useSelector(state => state.authState.color);
    const isOwner = useSelector(state => state.authState.isOwner);
    const layers = useSelector(state => state.gridtoolsState.layers);
    const selectedLayer = useSelector(state => state.gridtoolsState.selectedLayer);
    const selectedChar = useSelector(state => state.charactersState.selectedCharId);
    const enable3dDices = useSelector(state => state.gridtoolsState.enable3dDices);
    const lastActionTimer = useSelector(state => state.timersState.lastAction);
    const isTimerOpened = useSelector(state => state.timersState.opened);

    const [notifyTimer, setNotifyTimer] = React.useState(false);

    useEffect(() => {
        if (['play','pause','stop'].includes(lastActionTimer)) {
            setNotifyTimer(true);
        }
    }, [lastActionTimer]);

    useEffect(() => {
        let ctx = canvasRef.current.getContext('2d');
        let frameId = null;

        gridInstance.current = new GridController(ctx);
        networkInstance.current = new GridNetwork();
        
        gridInstance.current.resize();
        gridInstance.current.init();
        gridInstance.current.setNetwork(networkInstance.current);
        gridInstance.current.onChangeSelection((selectedItems) => dispatch(gridtoolsActions.setShapesSelected(selectedItems)));
        gridInstance.current.onDrawShape(() => dispatch(gridtoolsActions.setToolMode(Operations.SELECT)));
        gridInstance.current.onChangeLayer((layers) => dispatch(gridtoolsActions.setLayers(layers)));
        gridInstance.current.onSelectToken((charId) => dispatch(charactersActions.selectChar(charId,)));
        gridInstance.current.onZoomChange((zoom) => dispatch(gridtoolsActions.zoomChange(zoom)));
        gridInstance.current.onCenterChange((center) => dispatch(gridtoolsActions.centerChange(center)));
        gridInstance.current.onTokensChange((tokens) => dispatch(gridtoolsActions.setTokenPresence(tokens)));

        networkInstance.current.setTableId(tableId);
        networkInstance.current.setController(gridInstance.current);
        networkInstance.current.setDispatch(dispatch);

        const render = () => {
            gridInstance.current.draw();
            frameId = window.requestAnimationFrame(render);
        }
        render();

        const resize = () => {
            gridInstance.current.resize();
        }

        const onDragOver = (ev) => {
            ev.preventDefault();
            ev.dataTransfer.dropEffect = "link";
        }

        const onDrop = (ev) => {
            const token = ev.dataTransfer.getData("text/plain");
            if(token && token.length === 24) {
                gridInstance.current.addTokenByDrop(ev, token);
            }
        }

        const mouseDown = (e) => {gridInstance.current.mouseDown(e)};
        const mouseUp = (e) => gridInstance.current.mouseUp(e);
        const mouseMove = (e) => gridInstance.current.mouseMove(e);
        const preventContextMenu = (e) => e.preventDefault();
        const scrollEventFn = (e) => gridInstance.current.manageScroll(e);

        window.addEventListener('resize', resize);
        window.addEventListener('mousemove', mouseMove);
        window.addEventListener('mouseup', mouseUp);
        canvasEventRef.current.addEventListener('mousedown', mouseDown);
        canvasEventRef.current.addEventListener('contextmenu', preventContextMenu);
        canvasEventRef.current.addEventListener('wheel', scrollEventFn);
        canvasEventRef.current.addEventListener('dragover', onDragOver);
        canvasEventRef.current.addEventListener('drop', onDrop);

        let canvasElement = canvasEventRef.current;
            
        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', resize);
            canvasElement.removeEventListener('mousedown', mouseDown);
            canvasElement.removeEventListener('contextmenu', preventContextMenu);
            canvasElement.removeEventListener('wheel', scrollEventFn);
            canvasElement.removeEventListener('dragover', onDragOver);
            canvasElement.removeEventListener('drop', onDrop);
            window.removeEventListener('mousemove', mouseMove);
            window.removeEventListener('mouseup', mouseUp);
            gridInstance.current.clear();
            dispatch({type:'RESET'});
        }
    },[dispatch, tableId]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsTool) {
            if (gridtoolsTool === Operations.REMOVE) {
                gridInstance.current.removeSelected();
                dispatch(gridtoolsActions.setToolMode(Operations.SELECT));
            } else if (gridtoolsTool === Operations.CHANGE_LAYER) {
                gridInstance.current.changePrivateLayerSelected();
                dispatch(gridtoolsActions.setToolMode(Operations.SELECT));
            } else if (gridtoolsTool === Operations.BRING_TO_FRONT) {
                gridInstance.current.changeShapeOrder(true);
                dispatch(gridtoolsActions.setToolMode(Operations.SELECT));
            } else if (gridtoolsTool === Operations.SEND_TO_BACK) {
                gridInstance.current.changeShapeOrder(false);
                dispatch(gridtoolsActions.setToolMode(Operations.SELECT));
            } else {
                gridInstance.current.setToolMode(gridtoolsTool);
            }
        }
    }, [gridtoolsTool, dispatch]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsSnap) {
            gridInstance.current.setSnapMode(gridtoolsSnap);
        }
    }, [gridtoolsSnap]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsProps) {
            gridInstance.current.setToolProps(gridtoolsProps);
        }
    }, [gridtoolsProps]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsGridId) {
            networkInstance.current.setGridId(gridtoolsGridId);
        }
    }, [gridtoolsGridId]);

    useEffect(() => {
        if (gridInstance.current) {
            gridInstance.current.setSelectedChar(selectedChar);
        }
    }, [selectedChar]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsActiveGridId) {
            networkInstance.current.setActiveGridId(gridtoolsActiveGridId);
        }
    }, [gridtoolsActiveGridId]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsPropsGrid) {
            gridInstance.current.setPropsGrid(gridtoolsPropsGrid);
            networkInstance.current.setPropsGrid(gridtoolsPropsGrid);
        }
    }, [gridtoolsPropsGrid]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsLoadedGrid) {
            let render = [];
            let layers = [];

            try {
                if (typeof gridtoolsLoadedGrid.render === 'string') {
                    render = JSON.parse(gridtoolsLoadedGrid.render);
                } else {
                    render = gridtoolsLoadedGrid.render;
                }

                if (typeof gridtoolsLoadedGrid.layers === 'string') {
                    layers = JSON.parse(gridtoolsLoadedGrid.layers);
                } else {
                    layers = gridtoolsLoadedGrid.layers;
                }
            } catch (e) {
                console.error(Object.assign(e,{component:'Table/Grid', id: 'RenderAndLayers'}));
            }

            gridInstance.current.importRender(render);
            gridInstance.current.importLayers(layers);
        }
    }, [gridtoolsLoadedGrid]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsSyncRender) {
            gridInstance.current.importRender(gridtoolsSyncRender);
        }
    }, [gridtoolsSyncRender]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsSyncRenderOp) {
            gridInstance.current.importRenderOperetion(gridtoolsSyncRenderOp, gridtoolsSyncRenderItems);
        }
    }, [gridtoolsSyncRenderOp, gridtoolsSyncRenderItems]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsSyncPropsGrid) {
            gridInstance.current.setPropsGrid(gridtoolsSyncPropsGrid);
        }
    }, [gridtoolsSyncPropsGrid]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsSignalBuffer && gridtoolsSignalBuffer.length > 0) {
            gridInstance.current.receivingSignals(gridtoolsSignalBuffer);
            dispatch(gridtoolsActions.clearSignal());
        }
    }, [gridtoolsSignalBuffer, dispatch]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsMeasureBuffer) {
            gridInstance.current.receivingMeasure(gridtoolsMeasureBuffer);
        }
    }, [gridtoolsMeasureBuffer, dispatch]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsMovingBuffer) {
            gridInstance.current.receivingMoving(gridtoolsMovingBuffer);
        }
    }, [gridtoolsMovingBuffer, dispatch]);

    useEffect(() => {
        if (gridInstance.current && characterSeekForToken) {
            gridInstance.current.jumpToToken(characterSeekForToken.id);
        }
    }, [characterSeekForToken, dispatch]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsMeasureClearBuffer) {
            gridInstance.current.clearMeasure(gridtoolsMeasureClearBuffer);
        }
    }, [gridtoolsMeasureClearBuffer, dispatch]);

    useEffect(() => {
        if (gridInstance.current && gridtoolsPropagate > 0) {
            gridInstance.current.network.persistUpdate();
            gridInstance.current.network.propagateUpdate();
        }
    }, [gridtoolsPropagate]);

    useEffect(() => {
        if (gridInstance.current && characterList) {
            gridInstance.current.setCharacterList(characterList);
        }
    }, [characterList]);

    useEffect(() => {
        if (gridInstance.current && playerId) {
            gridInstance.current.setPlayerId(playerId);
        }
    }, [playerId]);

    useEffect(() => {
        if (gridInstance.current && playerColor) {
            gridInstance.current.setPlayerColor(playerColor);
        }
    }, [playerColor]);

    useEffect(() => {
        if (gridInstance.current && isOwner) {
            gridInstance.current.setIsOwner(isOwner);
        }
    }, [isOwner]);

    useEffect(() => {
        if (gridInstance.current && layers) {
            gridInstance.current.setLayers(layers, selectedLayer);
        }
    }, [layers, selectedLayer]);

    useEffect(() => {

        let resetkey = () => {
            gridInstance.current.zoomTo(7);
            return true;
        }
        let zoomInkey = () => {
            gridInstance.current.zoomIn(0);
            return true;
        }
        let zoomOutkey = () => {
            gridInstance.current.zoomOut(0);
            return true;
        }
        let recenter = () => {
            gridInstance.current.jumpToCenter();
            return true;
        }

        Keyboard.register({alt: true, key:'Z'}, resetkey);
        Keyboard.register({alt: true, key:'C'}, recenter);
        Keyboard.register({alt: true, key:'+'}, zoomInkey);
        Keyboard.register({alt: true, key:'-'}, zoomOutkey);

        const setFocusOnCanvas = (e) => {
            gridInstance.current.setFocusOnCanvas(e.target.tagName === 'CANVAS' || e.target.id === 'eventHandler');
        }

        window.addEventListener('click', setFocusOnCanvas);

        return () => {
            Keyboard.unregister({alt: true, key:'Z'}, resetkey);
            Keyboard.unregister({alt: true, key:'C'}, recenter);
            Keyboard.unregister({alt: true, key:'+'}, zoomInkey);
            Keyboard.unregister({alt: true, key:'-'}, zoomOutkey);
            window.removeEventListener('click', setFocusOnCanvas);
        }
    }, []);

    const zoomOut = () => {
        gridInstance.current.zoomOut();
    }

    const zoomIn = () => {
        gridInstance.current.zoomIn();
    }

    const jumpToCenter = () => {
        gridInstance.current.jumpToCenter();
    }

    const toggle3dDices = () => {
        dispatch(gridtoolsActions.toggle3dDiceEnable());
    }

    const toggleTurnOrder = () => {
        dispatch(turnorderActions.toggleTurnOrder());
    }

    const toggleTimers = () => {
        setNotifyTimer(false);
        dispatch(timersActions.toggleTimer());
    }


    const noEvent = (e) => {
        e.stopPropagation(); 
        e.preventDefault(); 
        return false;
    }

    return <Container>
        <canvas width="1000" height="1000" id="canvas2d" ref={canvasRef}></canvas>
        <Dice3d />
        <EventHandler id="eventHandler" ref={canvasEventRef}/>
        <ZoomOut title={Translate('gridtools.zoomout')} onClick={() => {zoomOut()}} onMouseDown={noEvent} onMouseUp={noEvent}>-</ZoomOut>
        <ZoomIcon />
        <ZoomIn title={Translate('gridtools.zoomin')} onClick={() => {zoomIn()}} onMouseDown={noEvent} onMouseUp={noEvent}>+</ZoomIn>
        <JumpToCenter title={Translate('gridtools.jumpcenter')} onClick={() => {jumpToCenter()}} onMouseUp={noEvent} />
        <Enable3D className={enable3dDices ? 'enabled' : 'disabled'} title={Translate('gridtools.' + (enable3dDices ? 'disable3dDices' : 'enable3dDices'))} onClick={() => {toggle3dDices()}} onMouseUp={noEvent}/>
        <TurnOrder title={Translate('gridtools.turnorder')} onClick={() => {toggleTurnOrder()}} onMouseUp={noEvent} />
        <Timers title={Translate('gridtools.timers')} onClick={() => {toggleTimers()}} onMouseUp={noEvent} className={notifyTimer && !isTimerOpened ? 'notify' : ''} />

        {gridtoolsProps && gridtoolsProps.gmMode && (
            <GmWarning><T value="gridtools.gmmode" /></GmWarning>
        )}
    </Container>;
};